import { registerJoinedCopy, registerTerminalLinks, selectionText } from './terminal-links';
import { useLinks } from '@src/components/links/LinkMenu';
// InteractiveTerminal.tsx
import '@src/styles/xterm.css';
import '@xterm/xterm/css/xterm.css';

import { useLingui } from '@lingui/react/macro';

import {
  AgenticProcessEventName,
  dataContext,
  FlowDataSource,
  fsStore,
  isProcessEnded,
  isProcessRunning,
  PrefKey,
  Shell,
  shellQuote,
  toplog,
  type AgenticProcess,
  Artifact,
  TypeId,
} from '@sdk';
import type { DisplayEntry } from '@sdk';
import { DockPointer } from '@src/navigation/DockPointer';
import { historyEntryDock } from '@src/pages/flow-page/display-stack';
import { PtySyncSession } from '@sdk/pty-sync/PtySyncSession.js';
import { claimTabSwitchReady, sinceTabSwitch } from '@src/navigation/tab-switch-state';
import { useScrollSync } from '@sdk/pty-sync/ui/useScrollSync.js';
import { XTermHarness } from '@sdk/pty-sync/ui/XTermHarness.js';
import { useContext } from '@src/hooks/useContext';
import { useEntity } from '@src/hooks/entity-hooks';
import { useInputDir } from '@src/hooks/use-input-dir';
import { useInstancePreferences } from '@src/hooks/use-instance-preferences';
import { usePreference } from '@src/hooks/use-preference';
import { useProcessArtifacts } from '@src/hooks/use-process-artifacts';
import { useDockNavigation, useSideWindows } from '@src/navigation';
import { useFS } from '@src/hooks/useFS';
import { useShell } from '@src/hooks/useShell';
import { FitAddon } from '@xterm/addon-fit';
import { useXtermShellAttach } from '../useXtermShellAttach';
import { releaseLaunchWatch } from '@src/components/agents/launch-watch';
import { SearchAddon } from '@xterm/addon-search';
import { Terminal as XTerm } from '@xterm/xterm';
import { useTheme } from 'next-themes';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AnnotationGutter } from './AnnotationGutter';
import { SurfaceSwitchWaitNotice } from './SurfaceSwitchWaitNotice';
import { useSurfaceSwitchWait } from './use-process-surface';
import { ColumnHeaderBar } from './ColumnHeaderBar';
import { PaneBar } from './PaneBar';
import { PaneSelectorBar } from './PaneSelectorBar';
import { PaneView } from './PaneView';
import { ProcessToolbar } from './ProcessToolbar';
import { ChatComposerBar } from './ChatComposerBar';
import { type CompactExecutionInputHandle } from '@src/components/entity-execution-panel/CompactExecutionInput';
import { ChatPlanModeProvider } from './chat-plan-mode-context';
import { FrozenWhenHidden } from './FrozenWhenHidden';
import { SimpleChatPane } from './SimpleChatPane';

import { useIsAdvanced } from '@src/components/view-mode';
import { useSessionSurface } from '@src/contexts/view-mode-context';
import { PtySyncProvider, usePtySyncSession } from './PtySyncContext';
import { TerminalRuntimeErrorBanner } from './TerminalRuntimeErrorBanner';
import {
  AnalysisPanel,
  GitPanel,
  InputFilesPanel,
  PromptIndexPanel,
  QueuePanel,
  SIDE_TABS,
  SideTabId,
  SimpleDirTree,
  SkillsAgentsPanel,
  parseSideTabId,
  usePromptsForProcess,
  type PromptEntry,
} from './side-windows';
import { SideTabTooltipContent } from './LastPromptTooltip';
import { TabbedSideDrawer, type TabDescriptor } from '@src/components/ui/side-drawer';
import { ShellTerminal } from './ShellTerminal';
import { TerminalBottomRibbon } from './TerminalBottomRibbon';
import { TerminalSearchBar } from './TerminalSearchBar';
import { calcTimeGutterWidth, TimeGutter } from './TimeGutter';
import { TraceGutter } from './TraceGutter';
import { getAnchors, useAnnotationGutter } from './use-annotation-gutter';
import { useTimeGutter } from './use-time-gutter';
import { useTraceGutter } from './use-trace-gutter';
import { EntityContextPanel } from '@src/components/entity-context';
import {
  clipboardDataHasImage,
  imageFilesFromClipboardItems,
  textFromClipboardItems,
} from '@src/utils/clipboard-image';
import { annotateImageFiles } from '@src/components/image-annotator/annotate-files';

export interface TraceFilters {
  events: boolean;
  time: boolean;
  index: boolean;
  line: boolean;
  absLine: boolean;
  debugTime: boolean;
  refTime: boolean;
  promptAnnotations: boolean;
}

export interface ColVisibility {
  trace: boolean;
  time: boolean;
  annotations: boolean;
}

import { perfLog } from '@src/routes/loaders/_perf';
import { DARK_THEME, LIGHT_THEME } from './terminalThemes';

// An empty bracketed paste (RFC 6093 start+end markers, no payload) — the exact
// signal an image paste delivers to the PTY, which the CLI reads the system
// clipboard on. Re-emitted after annotation so the CLI inlines the annotated image.
const bracketedPaste = (text: string) => `\x1b[200~${text}\x1b[201~`;
import { XTERM_BASE_OPTIONS, applyRtlGridContract, registerOsc52ClipboardWrite } from './terminalConfig';
import { retireXterm } from './retire-xterm';
import { workerCliVendor } from './process-cli-presentation';
import { isTextInputTarget } from '@src/utils/isTextInputTarget';

// Focus the terminal WITHOUT yanking focus from a text field the user is editing
// OUTSIDE it (e.g. the tab-strip rename input — an automatic blur auto-commits
// and cancels the rename). Only the AUTOMATIC focus paths (init, activate) use
// this; explicit user clicks still focus unconditionally.
function focusTerminalUnlessEditingElsewhere(term: XTerm | null, container: HTMLElement | null) {
  if (!term) return;
  const ae = document.activeElement;
  if (ae && ae !== document.body && !container?.contains(ae) && isTextInputTarget(ae)) return;
  term.focus();
}

interface InteractiveTerminalProps {
  sessionId: string;
  flow?: AgenticProcess | null;
  className?: string;
  active?: boolean;
  onTitleChange?: (title: string) => void;
  process?: AgenticProcess;
  /** Embedded mode: hides nav-out buttons (Open Terminal, Fork) in ProcessToolbar. */
  embedded?: boolean;
  /** Called by the close button shown in ProcessToolbar when embedded=true. */
  onClose?: () => void;
  /** Called whenever the worker session ID changes (or becomes null). */
  onWorkerSessionId?: (sessionId: string | null) => void;
}

/**
 * InteractiveTerminal - PTY-based interactive terminal component
 *
 * Note: flow prop is optional and only used for React.memo optimization
 * (prevents re-renders when flow object reference changes but ID is same).
 * Shell/PTY operations do NOT require flow - shell is infrastructure.
 */
const InteractiveTerminal: React.FC<InteractiveTerminalProps> = ({
  sessionId,
  flow: _flow = null,
  className = '',
  active = false,
  onTitleChange,
  process: propProcess,
  embedded,
  onClose,
  onWorkerSessionId,
}) => {
  // _flow is used only in the React.memo comparator below
  void _flow;

  const { t } = useLingui();
  const { agenticProcessTypeId, agenticProcess: contextProcess } = useContext();
  // Prop takes precedence (e.g. WorkflowsPage passes an explicit process).
  // For TabbedTerminal, no prop is passed — fall back to the context process
  // set by the loader, which is always authoritative for the active tab.
  const suppliedProcess = propProcess ?? contextProcess ?? undefined;
  // Lifecycle reconciliation lives in the always-mounted TerminalPanel, but
  // this memoized renderer still needs its own entity subscription. Process
  // instances mutate in place, so a parent re-render with the same process id
  // is intentionally swallowed by the comparator below; the subscription is
  // what updates every render-driving field (transport, shell, status, plan,
  // start failure, and transcript metadata) without moving lifecycle ownership
  // back under the startup gate.
  const { data: subscribedProcess } = useEntity<AgenticProcess>(suppliedProcess?.typeId ?? null);
  const process = subscribedProcess ?? suppliedProcess;
  const { navigation, currentDock } = useDockNavigation();
  const { resolvedTheme } = useTheme();
  // Skin layer: the view mode's SURFACE decides which pane shows (the session's
  // own switch is the selector). The xterm stays mounted underneath the chat
  // overlay (same session, same PTY — see SimpleChatPane), so switching is
  // instant and never resets the terminal. Embedded terminals (chat side panel)
  // and shell-only tabs (no AgenticProcess) always keep the xterm.
  const isAdvanced = useIsAdvanced();
  // Only the `chat` surface forces the pane. `vibe` renders VibeWorkspace, not
  // this component, so reaching here in vibe means the surfaces disagree (a
  // popped-out window, a mode-less process URL) — fall through to the process's
  // own transport via `isHeadless` below rather than pinning the pane, which
  // would put a chat over the live PTY of any task-runner process.
  const surface = useSessionSurface();
  const wantChat = surface === 'chat';
  // Headless (`pty_mode === false`): there is no PTY/xterm to skin — the chat
  // pane is the ONLY view. Force it on regardless of the surface, and (in the
  // render) skip mounting the xterm container entirely so no PtySync attach is
  // attempted for a process that has no shell.
  const isHeadless = !embedded && !!process && process.isHeadless;
  // Chat and the terminal are mutually exclusive: a chat surface over a live PTY
  // is only ever a switch on its way. When that switch has to WAIT (the agent is
  // mid-turn, or background work still runs in the PTY) the terminal stays on
  // screen with a line saying why, instead of a chat whose questions and
  // prompts would land in a terminal nobody sees.
  const switchWait = useSurfaceSwitchWait(process?.id);
  const showSimpleChat = wantChat ? !embedded && !!process && !switchWait : isHeadless;
  // The chat pane is a full render of the transcript (a 600-row chat ≈ 4 s). The surface
  // is GLOBAL — one view mode for the whole app — so every pooled panel, shown or not,
  // used to mount its pane when the mode flipped to chat and unmount it on the way back:
  // 11 s with three long chats pooled, on every switch between a chat tab and a terminal
  // tab (FLOWPAD-2193). A panel now mounts its pane the first time it is SHOWN as chat and
  // keeps it: when the surface moves on the pane is hidden, not rebuilt, so a flip is a
  // visibility change. `chatMounted` only ever goes true.
  //
  // Also not for a panel that is merely LEFT: on a switch the surface follows the NEW url
  // at once while the pool still lists the OLD panel as shown, and that panel's own mode
  // reconcile can flap its transport for a commit — either looked like "active in chat"
  // and mounted a 1373-row pane on the wrong tab. `active` drops on the next commit, which
  // cancels this frame.
  const [chatMounted, setChatMounted] = useState(false);
  const wantsChatMount = !chatMounted && showSimpleChat && active && !!process;
  useEffect(() => {
    if (!wantsChatMount) return;
    const frame = requestAnimationFrame(() => setChatMounted(true));
    return () => cancelAnimationFrame(frame);
  }, [wantsChatMount]);
  const mountChatPane = chatMounted && !!process;
  const targetTimestamp = currentDock?.transcriptTimestamp ?? undefined;

  const canUseDOM = typeof window !== 'undefined' && typeof document !== 'undefined';

  const xtermContainerRef = useRef<HTMLDivElement | null>(null);
  const terminalRef = useRef<XTerm | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const searchAddonRef = useRef<SearchAddon | null>(null);

  // pty-sync unified session (owns adapter, VT, segments)
  const ptySyncRef = useRef(new PtySyncSession());
  // `pty` toplog: only the first keystroke of a run dropped while disconnected is logged.
  const inputDropRunRef = useRef(false);
  const harnessRef = useRef<XTermHarness | null>(null);
  // Reactive snapshot — subscribes to ptySyncRef without needing PtySyncProvider context
  const ptySyncSnapshot = usePtySyncSession(ptySyncRef.current);

  const shellRef = useRef<Shell | null>(null);
  const terminalLinks = useLinks(shellRef, process);
  const firstPromptBufferRef = useRef('');
  const firstPromptReportedRef = useRef(false);

  const sessionIdRef = useRef(sessionId);

  const isTransitioningRef = useRef(false);
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [terminalReady, setTerminalReady] = useState(false);
  const [bufferFlushCount, setBufferFlushCount] = useState(0);
  const anchorsResolvedRef = useRef(false);

  const { preferences } = useInstancePreferences();
  const { shell } = useShell(sessionId);
  const [shellReady, setShellReady] = useState(false);
  // `tab_switch` needs both facts inside callbacks that outlive the render:
  // whether this panel is the shown one, and whether it already holds history
  // (a warm activation) or is still attaching (the cold `ready` comes from
  // `on_connected done`).
  const activeRef = useRef(active);
  activeRef.current = active;
  // Only the view on screen sizes the PTY — its one winsize is shared by every
  // client attached to it, and a hidden view's grid is stale.
  const onScreen = () => activeRef.current && document.visibilityState !== 'hidden';
  const shellReadyRef = useRef(false);
  shellReadyRef.current = shellReady;
  // With the chat pane over the xterm, the chat is what the user sees — its
  // `ready` comes from SimpleChatPane, not from the terminal underneath.
  const showSimpleChatRef = useRef(showSimpleChat);
  showSimpleChatRef.current = showSimpleChat;
  // Keep shellRef in sync so callbacks and hooks that capture shellRef still work.
  shellRef.current = shell;
  const processIsActive = process?.status ? isProcessRunning(process.status) : false;
  // The process ended (stopped / failed): no PTY to follow until it runs again, so the view gives
  // back what a live session holds — its watch, its output subscription, the shell's chunk window.
  // A plain shell (no process) and a process that has not started are not "ended".
  // This is the TERMINAL's hold only. In chat mode the chat pane's own hooks keep the process
  // watched after it ends, by design: "stopped" is a chat's resting state, and the watch is what
  // carries another window's status change and the watcher-only progress stream. A watch on an
  // ended process costs nothing — pushes are event-driven and nothing flows for it.
  const processEnded = !!process?.status && isProcessEnded(process.status);

  // Live failed-to-start latch → banner. The loader only classifies a latched
  // process on navigation; when the worker dies instantly while this tab is
  // already mounted (first death, or a failed Retry), the only signal is the
  // entity update flipping `start_failure`. The loader-context `process` is
  // not reactive, so subscribe via useEntity and surface the banner here.
  // ── Session surface ───────────────────────────────────────────────────────
  // The always-mounted TerminalPanel owns process transport reconciliation, so
  // this conditional renderer cannot lose a mode click while startup hides it.
  // This component's subscription above is render-only.
  const liveStartFailure = process?.start_failure ?? null;
  useEffect(() => {
    if (!liveStartFailure || !process) return;
    dataContext.setTerminalRuntimeError({
      kind: 'failed_to_start',
      processId: process.id,
      shellId: process.shell_id ?? null,
    });
  }, [liveStartFailure, process]);

  // Notify parent when the worker session ID becomes known (or clears)
  useEffect(() => {
    onWorkerSessionId?.(process?.session_id ?? null);
  }, [process?.session_id, onWorkerSessionId]);

  // The view's watch, held while the process is live and the view is mounted. Leaving either
  // way also gives back the launch-time lease its opener took (launch-watch.ts).
  useEffect(() => {
    if (!process || processEnded) return;
    const processId = process.id;
    let disposed = false;
    let unwatch: (() => Promise<void>) | null = null;
    void process.watch().then((release) => {
      if (disposed) {
        void release();
        return;
      }
      unwatch = release;
    });
    return () => {
      disposed = true;
      if (unwatch) void unwatch();
      releaseLaunchWatch(processId);
    };
  }, [process?.id, processEnded]);

  useEffect(() => {
    firstPromptBufferRef.current = '';
    firstPromptReportedRef.current = false;
  }, [process?.id]);
  const [cellHeight, setCellHeight] = useState(0);
  // cellHeight is derived from the observed xterm container's own offsetHeight and
  // flows back into overlays inside that container — so writing it unconditionally
  // from the ResizeObserver re-lays-out the container and re-fires the observer
  // (a self-referential ResizeObserver→setState→relayout loop that pegs the UI
  // when a sibling panel, e.g. the Analysis expand, perturbs the shared layout).
  // Commit only on a meaningful (>0.5px) change so the cycle converges and stops.
  const lastCellHeightRef = useRef(0);
  const commitCellHeight = useCallback((next: number) => {
    if (!Number.isFinite(next) || next <= 0) return;
    if (Math.abs(next - lastCellHeightRef.current) < 0.5) return;
    lastCellHeightRef.current = next;
    setCellHeight(next);
  }, []);
  const [traceFilters, setTraceFilters] = usePreference<TraceFilters>(PrefKey.TRACE_FILTERS);
  const [gutterExpanded, setGutterExpanded] = useState(false);
  const [colVis, setColVis] = usePreference<ColVisibility>(PrefKey.COLUMN_VISIBILITY);
  // Open side windows + active are dock state (URL: ?sideWindows&activeSideWindow),
  // read/written through the shared useSideWindows() hook. We narrow the generic
  // string ids back to this surface's SideTabId registry (dropping any stale or
  // foreign id) so the descriptor lookups below are total.
  const sideWindows = useSideWindows();
  const sideWindowTabs = useMemo(
    () => sideWindows.windows.filter((w): w is SideTabId => parseSideTabId(w) !== null),
    [sideWindows.windows],
  );
  const activeSideTab = useMemo<SideTabId | null>(() => {
    const parsed = parseSideTabId(sideWindows.active);
    return parsed && sideWindowTabs.includes(parsed) ? parsed : (sideWindowTabs[sideWindowTabs.length - 1] ?? null);
  }, [sideWindows.active, sideWindowTabs]);

  const [searchOpen, setSearchOpen] = useState(false);
  const [activePane, setActivePane] = useState<'claude' | 'shell'>('claude');
  const handlePasteRef = useRef<() => Promise<void>>(async () => {});

  // Open/close/select/toggle delegate to the shared hook (single URL writer).
  // Re-exposed under this surface's SideTabId-typed names so the JSX below is
  // unchanged; the hook itself is id-agnostic.
  const openSideTab = sideWindows.open;
  const closeSideTab = sideWindows.close;
  const selectSideTab = sideWindows.select;
  const toggleSideTab = sideWindows.toggle;

  // Input dir info for file attachment workflow.
  // Only fetch for the active tab — inactive pre-mounted terminals share the
  // same contextProcess, which caused N identical /input-dir API calls per
  // render cycle (amplified by re-render loops into 20+ redundant requests).
  const inputDirInfo = useInputDir(active ? process : undefined);
  const inputFs = useFS(inputDirInfo?.computeNodeTypeId ?? undefined);
  const inputFsRef = useRef(inputFs);
  inputFsRef.current = inputFs;
  const inputDirBrowse = inputDirInfo ? (inputFs?.browse(inputDirInfo.absPath) ?? null) : null;
  const fileCount = inputDirBrowse?.items.length ?? 0;

  // Sidecar shell state derived from entity
  const sidecarShellId = process?.sidecar_shell_id ?? null;

  const handleKillSidecar = useCallback(async () => {
    if (!process) return;
    process.sidecar_shell_id = null;
    await process.save();
    setActivePane('claude');
  }, [process]);

  const handleToggleSidecar = useCallback(async () => {
    if (!process) return;
    if (!sidecarShellId) {
      // Create a plain Shell entity and let ShellTerminal start its PTY
      const computeNodeId = shellRef.current?.compute_node_id ?? dataContext.computeNode?.id ?? null;
      const computeNodeUname = shellRef.current?.compute_node_uname ?? dataContext.computeNode?.uname ?? null;
      if (!computeNodeId) return;
      const workdir = process.workdir ?? shellRef.current?.workdir ?? undefined;
      const shell = new Shell({
        compute_node_id: computeNodeId,
        compute_node_uname: computeNodeUname,
        workdir: workdir ?? null,
      });
      await shell.save();
      process.sidecar_shell_id = shell.id;
      await process.save();
      setActivePane('shell');
    } else {
      setActivePane((p) => (p === 'shell' ? 'claude' : 'shell'));
    }
  }, [process, sidecarShellId]);

  const handlePaneSelect = useCallback(
    (pane: 'claude' | 'shell') => {
      if (pane === 'claude') {
        void handleKillSidecar();
      } else {
        setActivePane('shell');
      }
    },
    [handleKillSidecar],
  );

  // Reset to claude pane when sidecar is killed externally
  useEffect(() => {
    if (!sidecarShellId) setActivePane('claude');
  }, [sidecarShellId]);

  // Fetch input dir listing whenever it becomes available (for file count badge).
  // Use inputFsRef to avoid re-firing on every render (useFS returns a new object each time).
  useEffect(() => {
    if (!inputDirInfo || !inputFsRef.current) return;
    void inputFsRef.current.listDirectory(inputDirInfo.absPath);
  }, [inputDirInfo?.absPath]);

  // Paste handler — updated whenever inputDirInfo changes
  useEffect(() => {
    handlePasteRef.current = async () => {
      if (!inputDirInfo) return;
      try {
        const items = await navigator.clipboard.read();
        const [[captured], initialCaption] = await Promise.all([
          imageFilesFromClipboardItems(items, new Date(), { prefix: 'screenshot' }),
          textFromClipboardItems(items),
        ]);
        if (!captured) return;
        // Offer markup (and a caption, prefilled with any text that came along)
        // before the screenshot is attached. Cancel aborts.
        const {
          files: [file],
          caption,
        } = await annotateImageFiles([captured], { initialCaption });
        if (!file) return;

        const uploads = await fsStore
          .getState()
          .uploadFiles(inputDirInfo.computeNodeTypeId, inputDirInfo.absPath, [file]);
        await Promise.all(
          uploads.map((upload: { waitForCompletion: () => Promise<unknown> }) => upload.waitForCompletion()),
        );
        // By now the annotated PNG is on the system clipboard (written inside the
        // Save gesture; the upload above outlasts that write). Re-emit the empty
        // bracketed paste so the CLI re-reads the clipboard and inlines the
        // ANNOTATED image. The original paste-time signal was suppressed (the
        // capture-phase paste listener), so the CLI never saw the original.
        await shellRef.current?.sendInput(bracketedPaste(''));
        // Full-resolution fallback: the inline copy the CLI keeps may be downsized,
        // so also reference the file by path.
        const fullPath = `${inputDirInfo.absPath}/${file.name}`;
        await shellRef.current?.sendInput(`\nFile ${file.name} is available here: ${fullPath}\n`);
        // The caption rides as a note after the file line — bracketed, so a
        // multi-line note is typed into the prompt, never submitted line by line.
        if (caption) await shellRef.current?.sendInput(bracketedPaste(`User note: ${caption}`));
        openSideTab(SideTabId.Files);
        // Opening the Files drawer (and the annotate dialog before it) drops
        // focus to <body> — hand it back to the terminal so typing continues.
        requestAnimationFrame(() => terminalRef.current?.focus());
      } catch {
        // clipboard read failed — fall through to default xterm behavior
      }
    };
  }, [inputDirInfo, openSideTab]);

  // Scroll metrics from pty-sync library (firstVisibleRow, cellHeight, etc.)
  const metrics = useScrollSync(terminalRef.current, ptySyncSnapshot.adapter, []);
  const viewportY = metrics?.firstVisibleRow ?? 0;
  const metricsCellHeight = metrics?.cellHeight ?? cellHeight;
  const rows = metrics?.visibleRows ?? terminalRef.current?.rows ?? 24;

  const {
    entries: gutterEntries,
    totalTraceEvents,
    historicalCount,
    liveCount,
    sessionStartTime,
    allEvents: allTraceEvents,
  } = useTraceGutter(process ?? null, terminalReady, ptySyncRef.current, shellReady, ptySyncSnapshot.version);
  const lastMessageTime = useMemo(() => {
    // Latest history event timestamp — used to anchor the "time since last
    // message" indicator.
    const last = allTraceEvents
      .filter((e) => e.source === FlowDataSource.History)
      .reduce<string | null>((max, e) => (max === null || e.timestamp > max ? e.timestamp : max), null);
    return last;
  }, [allTraceEvents]);
  const showGutter = !!process && traceFilters.events && colVis.trace;
  // Reserve gutter space based on user settings even before process resolves,
  // so xterm fits at the correct width from the start (avoids layout shift).
  const reserveGutterSpace = traceFilters.events && colVis.trace;
  const showTimeGutter =
    !!process &&
    colVis.time &&
    (traceFilters.time ||
      traceFilters.index ||
      traceFilters.line ||
      traceFilters.absLine ||
      traceFilters.debugTime ||
      traceFilters.refTime);
  const reserveTimeGutterSpace =
    colVis.time &&
    (traceFilters.time ||
      traceFilters.index ||
      traceFilters.line ||
      traceFilters.absLine ||
      traceFilters.debugTime ||
      traceFilters.refTime);
  const timeGutterWidth = showTimeGutter || reserveTimeGutterSpace ? calcTimeGutterWidth(traceFilters) : 0;

  const {
    elements: rawAnnotationElements,
    createBookmark,
    createComment,
    deleteBookmark,
    pendingScrollLine,
    sessionAnnotations,
  } = useAnnotationGutter(
    process?.session_id,
    terminalReady,
    ptySyncSnapshot.adapter,
    ptySyncRef.current,
    targetTimestamp,
    shellReady,
    ptySyncSnapshot.version,
    process?.workdir ?? undefined,
  );
  const annotationElements = useMemo(
    () =>
      traceFilters.promptAnnotations
        ? rawAnnotationElements
        : rawAnnotationElements.filter((el) => el.kind !== 'prompt'),
    [rawAnnotationElements, traceFilters.promptAnnotations],
  );
  const showAnnotationGutter = isAdvanced && !!process?.session_id && colVis.annotations;
  const reserveAnnotationSpace = isAdvanced && colVis.annotations;

  // Single source of truth: the entity's persisted ``plan_path``.
  // listen.py's ExitPlanMode hook + the PTY-trigger getPlan() flow both
  // write to the same field and broadcast the entity-update over WS, so
  // the button flips on within ms of plan detection from any source.
  const hasPlan = !!process?.plan_path;

  const handleOpenLastPlan = useCallback(() => {
    if (!agenticProcessTypeId || !process?.plan_path) return;
    navigation.openPlan(agenticProcessTypeId, process.plan_path);
  }, [process, agenticProcessTypeId, navigation]);

  // Artifacts chip: what the run REGISTERED (`flow artifact …`), read off the
  // process property and kept live by the `artifact.*` bus lane. It replaces
  // the markdown-docs chip, which inferred "the run produced something" from an
  // authored `.md` instead of the run saying so.
  const { data: artifacts } = useProcessArtifacts(process);
  // Opens the referenced asset via the shared file dispatch; an artifact with no
  // `asset_ref` (a webapp, say) opens as an app tab addressed by the artifact.
  const handleOpenArtifact = useCallback(
    (artifact: Artifact) => {
      if (artifact.asset_ref) navigation.openFile(artifact.asset_ref);
      else navigation.openDock(DockPointer.forAppEntity(new TypeId(`${Artifact.type}-${artifact.id}`)));
    },
    [navigation],
  );

  // Shown chip: every `flow show` this run made (`context_data.display_stack`).
  // A row opens the target as its own tab, rebased onto the process's project
  // — the same address the show listener mints, so it lands on that tab.
  const shownStack = process?.displayStack;
  const handleOpenShown = useCallback(
    (entry: DisplayEntry) => {
      const dock = historyEntryDock(entry, process?.project_id ?? null);
      if (dock) navigation.openDock(dock);
    },
    [process, navigation],
  );

  // On mount (and whenever the process identity changes), proactively call
  // getPlan() once so the button restores after a reload — the line trigger
  // only fires for live new output, but ``plan_path`` survives persistence.
  useEffect(() => {
    if (!process || process.plan_path) return;
    // Fire-and-forget on mount, so it owns its failure: a backend that is down
    // (or absent, as under vitest) would otherwise raise an unhandled rejection
    // with no caller to receive it. No plan simply means the button stays put.
    void process.getPlan().catch(() => undefined);
    // We intentionally only react to the process identity changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [process?.id]);

  // Refresh-driven plan-detection: subscribe to status transitions and
  // run getPlan() once at registration. Server-side resolves the plan
  // from the JSONL transcript and persists plan_path on the entity; the
  // next entity-update over WS makes ``hasPlan`` flip to true automatically.
  useEffect(() => {
    if (!process) return;
    return process.onPlan({ validate: true }, () => {});
  }, [process]);

  // Canonical user-prompt list comes from the JSONL transcript via the
  // ``transcript/prompts`` server action — that's what Claude actually saw,
  // so it survives hook misses and WS drops that historically made the
  // annotation+trace merge unreliable. Annotations remain as an auxiliary
  // index for terminal-row anchoring (click-to-scroll).
  const { transcriptPrompts, refresh: refreshPrompts } = usePromptsForProcess(process ?? null);

  // Hold the latest `refreshPrompts` in a ref so the xterm `onData` effect
  // doesn't have to re-subscribe whenever the hook returns a new identity.
  const refreshPromptsRef = useRef<typeof refreshPrompts>(refreshPrompts);
  useEffect(() => {
    refreshPromptsRef.current = refreshPrompts;
  }, [refreshPrompts]);

  // Debounced refetch fired ~1s after the user presses Enter in the terminal.
  // Mashing Enter coalesces into one fetch 1s after the last keystroke.
  const enterRefetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleEnterRefetch = useCallback(() => {
    if (enterRefetchTimerRef.current) clearTimeout(enterRefetchTimerRef.current);
    enterRefetchTimerRef.current = setTimeout(() => {
      enterRefetchTimerRef.current = null;
      refreshPromptsRef.current?.({ force: true });
    }, 1000);
  }, []);
  useEffect(
    () => () => {
      if (enterRefetchTimerRef.current) clearTimeout(enterRefetchTimerRef.current);
    },
    [],
  );

  // Build a {timestamp → absRow} index from prompt annotations so we can
  // attach click-to-scroll behavior to transcript prompts whose annotation
  // exists. Missing annotations are graceful — the prompt still renders.
  const promptAnnotationIndex = useMemo<Array<{ time: number; absRow: number }>>(() => {
    const idx: Array<{ time: number; absRow: number }> = [];
    for (const el of rawAnnotationElements) {
      if (el.kind !== 'prompt' || !el.annotation || el.absRow == null) continue;
      const iso = el.annotation.created_date as unknown as string;
      const t = Date.parse(iso);
      if (!Number.isFinite(t)) continue;
      idx.push({ time: t, absRow: el.absRow });
    }
    idx.sort((a, b) => a.time - b.time);
    return idx;
  }, [rawAnnotationElements]);

  const mergedPrompts = useMemo<PromptEntry[]>(() => {
    const ABS_ROW_TOLERANCE_MS = 1000;
    return transcriptPrompts.map((tp) => {
      let absRow: number | null = null;
      const t = Date.parse(tp.time);
      if (Number.isFinite(t)) {
        let bestDelta = ABS_ROW_TOLERANCE_MS;
        for (const a of promptAnnotationIndex) {
          const d = Math.abs(a.time - t);
          if (d <= bestDelta) {
            bestDelta = d;
            absRow = a.absRow;
          }
        }
      }
      return { absRow, text: tp.text, time: tp.time, source: 'transcript' as const };
    });
  }, [transcriptPrompts, promptAnnotationIndex]);

  // Most recent prompt (transcript order is chronological) — shown in the
  // Prompts-icon hover card on both the ribbon and the side window.
  const lastPromptText = mergedPrompts.length ? mergedPrompts[mergedPrompts.length - 1].text : null;

  const timeGutterRows = useTimeGutter(ptySyncSnapshot.vt, viewportY, rows, ptySyncSnapshot.version);

  const scrollAnnotationToLine = useCallback((absoluteLine: number) => {
    ptySyncRef.current.getSnapshot().adapter?.scrollToRow(absoluteLine);
  }, []);

  const onAnnotationHoverRow = useCallback((absRow: number | null) => {
    const adapter = ptySyncRef.current.getSnapshot().adapter;
    if (!adapter) return;
    harnessRef.current?.setHoverHighlight(absRow, adapter.getEvictionOffset());
  }, []);

  // Scroll to annotation line when resolved by useAnnotationGutter.
  // Wait until the buffer actually contains the target row — during replay
  // the buffer grows progressively and the row may not exist yet.
  useEffect(() => {
    if (pendingScrollLine === null) return;
    const adapter = ptySyncRef.current.getSnapshot().adapter;
    if (!adapter) return;
    const maxRow = adapter.getEvictionOffset() + adapter.getBufferLength();
    if (pendingScrollLine <= maxRow) {
      scrollAnnotationToLine(pendingScrollLine);
    }
  }, [pendingScrollLine, scrollAnnotationToLine, bufferFlushCount]);

  // Rebuild anchor-based segments after replay is complete and annotations are loaded.
  // If no annotations, fall back to a default segment: [sessionStartTime → last transcript event].
  // bufferFlushCount is bumped by the output handler on each write until anchors resolve,
  // re-triggering this effect as content streams in (e.g. after page refresh).
  useEffect(() => {
    if (!terminalReady || !shellReady || !bufferFlushCount || !ptySyncSnapshot.adapter) return;
    const anchors = getAnchors(sessionAnnotations);
    if (anchors.length > 0) {
      ptySyncRef.current.buildSegmentsFromAnchors(anchors);
      anchorsResolvedRef.current = ptySyncRef.current.hasAnchorSegments();
    } else if (sessionStartTime) {
      const lastEventMs = allTraceEvents
        .filter((e) => e.source === FlowDataSource.History)
        .reduce((max, e) => Math.max(max, new Date(e.timestamp).getTime()), 0);
      if (lastEventMs > 0) {
        ptySyncRef.current.finalizeDefaultSegment(lastEventMs);
      }
    }
  }, [
    sessionAnnotations,
    terminalReady,
    shellReady,
    ptySyncSnapshot.adapter,
    sessionStartTime,
    allTraceEvents,
    bufferFlushCount,
  ]);

  // The size this view last delivered to the PTY. Output drawn while the xterm is at another size
  // is garbage that re-sending the PTY's own size can't fix (no winsize change → no SIGWINCH), so
  // that view asks for a redraw on its next send (`drift`).
  const syncedSizeRef = useRef<{ cols: number; rows: number } | null>(null);
  const driftRef = useRef(false);

  const handlePtyResize = useCallback(
    (cols: number, rows: number) => {
      if (isTransitioningRef.current) return;
      if (!onScreen()) return;
      const shell = shellRef.current;
      if (!shell) return;
      const repaint = driftRef.current;
      driftRef.current = false;
      syncedSizeRef.current = { cols, rows };
      // Not gated on `connected`: the shell keeps the size and its attach asserts it.
      void shell.resize(cols, rows, { repaint }).then(
        (ok) => {
          if (ok) return;
          syncedSizeRef.current = null;
          driftRef.current = true;
        },
        (e) => console.error('[InteractiveTerminal] Failed to resize PTY:', e),
      );
    },
    [sessionId],
  );

  /** Fit the xterm to its box and record the cell height. */
  const fitXterm = useCallback(() => {
    const term = terminalRef.current;
    const fit = fitAddonRef.current;
    if (!term || !fit) return;
    fit.fit();
    const container = xtermContainerRef.current;
    if (container?.offsetHeight && term.rows > 0) commitCellHeight(container.offsetHeight / term.rows);
  }, [commitCellHeight]);

  /** Fit the xterm and size the PTY to it — never one without the other. Off screen the view does
   *  not size the PTY, so it does not fit either; the return to screen fits + sends. */
  const fitAndSync = useCallback(() => {
    const term = terminalRef.current;
    if (!term || !onScreen()) return;
    try {
      fitXterm();
    } catch {
      return;
    }
    handlePtyResize(term.cols, term.rows);
  }, [fitXterm, handlePtyResize]);

  /** Live output reaching an off-screen xterm that is not at the size it last gave the PTY. */
  const noteDrift = useCallback(() => {
    if (driftRef.current || onScreen()) return;
    const term = terminalRef.current;
    const synced = syncedSizeRef.current;
    // Never synced: its attach sizes the PTY (and repaints) when it comes on screen.
    if (!term || !synced || (synced.cols === term.cols && synced.rows === term.rows)) return;
    driftRef.current = true;
    toplog.log(
      'pty',
      `fit_unsent shell=${sessionId} xterm=${term.cols}x${term.rows} synced=${synced.cols}x${synced.rows}`,
    );
  }, [sessionId]);

  // Refit terminal when annotation gutter or file panel appears/disappears — and size the PTY
  // to it: a refit the PTY never hears of leaves the xterm drawing output meant for another grid.
  useEffect(() => {
    if (!terminalReady) return;
    requestAnimationFrame(fitAndSync);
  }, [showAnnotationGutter, showTimeGutter, timeGutterWidth, terminalReady, sideWindowTabs.length, fitAndSync]);

  // Update refs on session change
  useEffect(() => {
    isTransitioningRef.current = true;

    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
      resizeTimeoutRef.current = null;
    }

    sessionIdRef.current = sessionId;

    // Reset VT state on session switch
    ptySyncRef.current.resetSession();
    setBufferFlushCount(0);
    anchorsResolvedRef.current = false;

    const t = setTimeout(() => {
      isTransitioningRef.current = false;

      // After the transition guard lifts, re-fit the terminal so it picks up
      // any container resize events that were suppressed during the window.
      requestAnimationFrame(fitAndSync);
    }, 150);

    return () => clearTimeout(t);
  }, [sessionId, fitAndSync]);

  // Shell entity is resolved reactively by useShell(sessionId) above.

  // Recalibrate Phase-1 segment when the real session start time becomes known.
  // sessionStartTime now comes from the earliest FlowData timestamp on
  // process.flowDataStream (vendor-agnostic), not a Claude-specific record.
  useEffect(() => {
    if (!sessionStartTime || !ptySyncSnapshot.adapter) return;
    const startMs = new Date(sessionStartTime).getTime();
    const term = terminalRef.current;
    const rows = term ? Math.max(3, term.rows - 4) : 3;
    ptySyncRef.current.initSegments(startMs, rows, ptySyncSnapshot.adapter.getEvictionOffset());
  }, [sessionStartTime, ptySyncSnapshot.adapter]);

  /**
   * Terminal init/dispose
   */
  useLayoutEffect(() => {
    if (!canUseDOM || !xtermContainerRef.current) return;

    const container = xtermContainerRef.current;

    if (terminalRef.current) {
      return;
    }

    // Suppress IMAGE pastes from reaching xterm/the PTY. The Cmd+V keydown handler
    // already routes image pastes through `handlePasteRef` (annotate → upload →
    // re-emit the paste). Letting the browser's native `paste` event also flow into
    // xterm would forward a bracketed paste to the PTY at paste-time, making the CLI
    // read the system clipboard before annotation — i.e. the pre-annotation original.
    // Eating image pastes here keeps the annotated image the single source of truth.
    // Text pastes pass through.
    const onDomPaste = (e: ClipboardEvent) => {
      if (clipboardDataHasImage(e.clipboardData)) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };

    let disposed = false;
    let fitTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let dimensionCheckTimeout: ReturnType<typeof setTimeout> | null = null;

    const waitForDimensions = () => {
      if (disposed) return;

      if (!container.offsetWidth || !container.offsetHeight) {
        dimensionCheckTimeout = setTimeout(waitForDimensions, 10);
        return;
      }

      initializeTerminal();
    };

    const initializeTerminal = () => {
      if (disposed) return;
      if (active) {
        perfLog('xterm initializeTerminal (active)');
      }

      const term = new XTerm({ ...XTERM_BASE_OPTIONS, scrollback: 50000 });

      const fit = new FitAddon();
      term.loadAddon(fit);

      const search = new SearchAddon();
      term.loadAddon(search);
      searchAddonRef.current = search;

      term.attachCustomKeyEventHandler((event: KeyboardEvent) => {
        if (event.type === 'keydown' && (event.ctrlKey || event.metaKey)) {
          if (event.key === 'c' && term.hasSelection()) {
            // Written here, so the browser's own copy (and the copy listener) must not run again.
            event.preventDefault();
            navigator.clipboard.writeText(selectionText(term)).catch(() => {});
            term.clearSelection();
            return false;
          }
          if (event.key === 'v') {
            void handlePasteRef.current();
            return false;
          }
          if (event.key === 'f') {
            // Don't send ^F to the PTY; let the event bubble to the container's
            // keydown handler, which preventDefaults and opens the search bar.
            return false;
          }
        }
        return true;
      });

      try {
        term.open(container);
        registerTerminalLinks(term, terminalLinks.handlers);
        registerJoinedCopy(term);
        // Stamp the RTL/bidi contract on THIS container. The vendor-keyed
        // effect below re-decides when worker_type resolves, but it cannot be
        // the only writer: the container is conditionally mounted
        // (`!isHeadless`), so a chat⇄terminal round trip mounts a fresh div
        // while worker_type never changes — leaving it bare, and every
        // pre-reversed Hebrew row reordered a second time by the browser.
        applyRtlGridContract(container, workerCliVendor(process?.worker_type));
        terminalRef.current = term;
        fitAddonRef.current = fit;
        container.addEventListener('paste', onDomPaste, true);
        if (active) {
          perfLog('term.open() done (active)');
        }
        // A mount on a tab switch means the "warm" terminal was thrown away.
        toplog.log('pty', `xterm_mount shell=${sessionId} active=${active} size=${term.cols}x${term.rows}`);
      } catch (e) {
        console.error('[InteractiveTerminal] Failed to open terminal:', e);
        return;
      }

      term.options.theme = resolvedTheme === 'dark' ? DARK_THEME : LIGHT_THEME;

      try {
        term.parser.registerCsiHandler({ final: 'c', prefix: '?' }, () => true);
        term.parser.registerCsiHandler({ final: 'I' }, () => true);
        term.parser.registerCsiHandler({ final: 'O' }, () => true);
        registerOsc52ClipboardWrite(term);
      } catch (e) {
        console.warn('[InteractiveTerminal] Parser API not available:', e);
      }

      term.onTitleChange((title: string) => {
        onTitleChange?.(title);
      });

      const tFitScheduled = performance.now();
      fitTimeoutId = setTimeout(() => {
        if (!disposed) {
          const tFitFired = performance.now();
          // Fired far past its 50ms means the main thread was busy, not this work.
          if (active) {
            toplog.log(
              'agentic_process.load',
              `InteractiveTerminal fit timer fired after ${(tFitFired - tFitScheduled).toFixed(0)}ms (scheduled 50ms) shell=${sessionId}`,
            );
          }
          try {
            fitXterm();

            // Initialize pty-sync session (adapter + VirtualTerminal)
            ptySyncRef.current.initialize(term);
            const adapter = ptySyncRef.current.getSnapshot().adapter;
            if (adapter) harnessRef.current = new XTermHarness(term, adapter);

            // Init Phase-1 segment for trace event row bucketing.
            // startTime is set to now; recalibrated below once shell entity loads.
            ptySyncRef.current.initSegments(Date.now(), Math.max(3, term.rows - 4), adapter?.getEvictionOffset() ?? 0);

            if (active) {
              toplog.log(
                'agentic_process.load',
                `InteractiveTerminal fit + ptySync.initialize took ${(performance.now() - tFitFired).toFixed(0)}ms shell=${sessionId}`,
              );
              perfLog('setTerminalReady(true) (active)');
            }
            setTerminalReady(true);
            focusTerminalUnlessEditingElsewhere(term, xtermContainerRef.current);

            requestAnimationFrame(() => {
              if (!disposed) {
                try {
                  fit.fit();
                } catch {
                  /* ignore */
                }
              }
            });
          } catch (e) {
            console.warn('[InteractiveTerminal] ❌ Failed to fit terminal on init:', e);
            setTerminalReady(true);
          }
        }
      }, 50);
    };

    waitForDimensions();

    return () => {
      disposed = true;

      container.removeEventListener('paste', onDomPaste, true);

      if (dimensionCheckTimeout) {
        clearTimeout(dimensionCheckTimeout);
        dimensionCheckTimeout = null;
      }

      if (fitTimeoutId) {
        clearTimeout(fitTimeoutId);
        fitTimeoutId = null;
      }

      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
        resizeTimeoutRef.current = null;
      }

      setTerminalReady(false);

      if (terminalRef.current) {
        const term = terminalRef.current;
        toplog.log('pty', `xterm_dispose shell=${sessionId} active=${active}`);
        retireXterm(term, (e) => console.warn('[InteractiveTerminal] Error disposing terminal:', e));

        terminalRef.current = null;
        fitAddonRef.current = null;
        searchAddonRef.current = null;
        ptySyncRef.current.dispose();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, isHeadless]);

  // Which RTL/bidi contract a terminal follows depends on the CLI running in
  // it, and worker_type arrives with the subscribed process — i.e. after
  // term.open() has already run. Deciding in its own layout effect re-applies
  // the contract the moment the vendor resolves, so a codex session never
  // paints a frame under Claude Code's contract (and back, if it ever swaps).
  useLayoutEffect(() => {
    const container = xtermContainerRef.current;
    if (container) applyRtlGridContract(container, workerCliVendor(process?.worker_type));
  }, [process?.worker_type]);

  // Intercept Cmd+F / Ctrl+F at the native DOM level so we can call preventDefault()
  // before the browser opens its own find bar. xterm's attachCustomKeyEventHandler
  // cannot do this — returning false there only suppresses xterm's processing,
  // not the browser's native behavior.
  useEffect(() => {
    const container = xtermContainerRef.current;
    if (!container) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape') {
        setSearchOpen((open) => {
          if (open) {
            terminalRef.current?.focus();
            return false;
          }
          return open;
        });
      }
    };
    container.addEventListener('keydown', onKeyDown);
    return () => container.removeEventListener('keydown', onKeyDown);
  }, [terminalReady]);

  // Theme updates
  useEffect(() => {
    const term = terminalRef.current;
    if (!term) return;
    term.options.theme = resolvedTheme === 'dark' ? DARK_THEME : LIGHT_THEME;
  }, [resolvedTheme]);

  // The owning TerminalPanel starts/attaches an AgenticProcess after the route
  // commits. Shell WS reconnects remain owned by Shell's ConnectionManager
  // on_reconnected listener.

  // Ref so the output handler always reads the latest bufferSyncUpdates without
  // requiring the effect to re-subscribe when the setting changes.
  const bufferSyncUpdatesRef = useRef(preferences.bufferSyncUpdates);
  bufferSyncUpdatesRef.current = preferences.bufferSyncUpdates;

  // Synchronized output (DEC 2026): a TUI brackets a frame with BSU … ESU; holding the bracketed
  // bytes until ESU paints the frame at once instead of tearing. Behind the bufferSyncUpdates pref.
  const syncRef = useRef({ buf: '', inSync: false, timer: null as ReturnType<typeof setTimeout> | null });

  const writeToTerm = useCallback((data: string) => {
    const term = terminalRef.current;
    if (!term) return;
    try {
      term.write(data, () => {
        if (!anchorsResolvedRef.current) {
          ptySyncRef.current.notifyBufferReady();
          setBufferFlushCount((c) => c + 1);
        }
      });
    } catch (e) {
      console.error('[InteractiveTerminal] write failed:', e);
    }
  }, []);

  /** Write out a bracketed frame still held (a disconnect, the view letting go). */
  const flushSyncFrame = useCallback(() => {
    const sync = syncRef.current;
    if (sync.timer) clearTimeout(sync.timer);
    sync.timer = null;
    if (sync.buf) writeToTerm(sync.buf);
    sync.buf = '';
    sync.inSync = false;
  }, [writeToTerm]);

  const writeLive = useCallback(
    (data: string) => {
      noteDrift();
      const BSU = '\x1b[?2026h';
      const ESU = '\x1b[?2026l';
      const sync = syncRef.current;
      if (!bufferSyncUpdatesRef.current) {
        writeToTerm(data);
        return;
      }
      if (!sync.inSync) {
        const bsuIdx = data.indexOf(BSU);
        if (bsuIdx < 0) {
          writeToTerm(data);
          return;
        }
        if (bsuIdx > 0) writeToTerm(data.slice(0, bsuIdx));
        sync.inSync = true;
        sync.buf = data.slice(bsuIdx);
        sync.timer = setTimeout(() => flushSyncFrame(), 2000); // a frame never closed is shown anyway
      } else {
        sync.buf += data;
      }
      const esuIdx = sync.buf.indexOf(ESU);
      if (esuIdx >= 0) {
        const endIdx = esuIdx + ESU.length;
        const frame = sync.buf.slice(0, endIdx);
        const remainder = sync.buf.slice(endIdx);
        if (sync.timer) clearTimeout(sync.timer);
        sync.timer = null;
        sync.buf = '';
        sync.inSync = false;
        writeToTerm(frame);
        if (remainder) writeLive(remainder);
      }
    },
    [writeToTerm, flushSyncFrame, noteDrift],
  );

  // The PTY in this view: its recorded past, then live — the ONE attach every terminal view uses
  // (useXtermShellAttach: connect / mount / recovery / reconnect). Gated on terminalReady so the
  // replay lands at the fitted size, not 80×24. PtySync sees every chunk; live output goes through
  // the synchronized-output writer above.
  // An ended process's view is detached; the xterm keeps its final screen and scrollback, and a
  // restart attaches again the way a mount does.
  useXtermShellAttach(processEnded ? null : shell, terminalReady ? terminalRef.current : null, {
    ready: terminalReady,
    onChunk: (chunk) => ptySyncRef.current.processChunk(chunk),
    write: writeLive,
    recoveredFor: (msg) => Boolean(process && msg?.process_id === process.id),
    ownsSize: onScreen,
    onAttached: ({ source, wrote, ms, historyKb }) => {
      // On screen the attach asserted this xterm's size and the program repainted at it.
      const t = terminalRef.current;
      if (t && onScreen()) {
        syncedSizeRef.current = { cols: t.cols, rows: t.rows };
        driftRef.current = false;
      }
      // Signal buffer ready once xterm has processed the history and backlog writes.
      if (wrote) {
        terminalRef.current?.write('', () => {
          anchorsResolvedRef.current = false;
          ptySyncRef.current.notifyBufferReady();
          setBufferFlushCount((c) => c + 1);
        });
      }
      setShellReady(true);
      if (activeRef.current && !showSimpleChatRef.current && toplog.isOn('tab_switch') && claimTabSwitchReady()) {
        toplog.log(
          'tab_switch',
          `ready ${sinceTabSwitch()} kind=terminal mode=cold source=${source} shell=${sessionId} attach_ms=${ms.toFixed(0)} history_kb=${historyKb.toFixed(0)}`,
        );
      }
    },
    onDetached: () => {
      setShellReady(false);
      flushSyncFrame();
    },
  });

  // The chunk window only feeds a live view (re-attach backlog, VT rebuild on resize); the
  // backend recording is untouched and a restart fills a fresh window.
  useEffect(() => {
    if (processEnded) shell?.releaseOutput();
  }, [processEnded, shell]);

  // ── The dock's command, typed once the PTY is actually at a prompt ─────────
  //
  // `?startCommand=` names a command the navigation asked
  // this terminal to type (see START_COMMAND_PARAM). The write lands HERE, on
  // the mounted view at `shellReady`, and nowhere else: the click handler that
  // navigated has no PTY yet, and the loader must not await one — a shell is
  // created, then attached, and bytes sent before the attach handshake
  // completes are simply dropped by a PTY that has not reached its prompt.
  //
  // Consumed exactly once per arrival. The ref guards a re-render, and the
  // `replace` navigation drops the param from the URL so a reload — or a back
  // button — cannot retype it. That matters more than tidiness: `startCommand`
  // SUBMITS, and silently re-running a command on refresh is not something a
  // URL should do.
  const startCommandRef = useRef<string | null>(null);
  useEffect(() => {
    if (!active || !shellReady || !sessionId) return;
    // Only the dock we are actually showing may type into us. Warm-mounted
    // panels for other tabs share this component, and a command addressed to
    // one terminal must never land in another.
    //
    // Through `shellId`, because the dock spells a shell as its TypeId
    // (`shell-<uuid>`) while `sessionId` here is the bare uuid — comparing the
    // two raw never matched, and the command was silently never typed.
    if (currentDock?.shellId !== sessionId) return;
    const command = currentDock?.shellStartCommand ?? null;
    // The param is gone once consumed, so a LATER arrival carrying the same
    // command (Run pressed twice on one code block) is a new ask, not a re-render.
    if (!command) {
      startCommandRef.current = null;
      return;
    }
    if (startCommandRef.current === command) return;
    startCommandRef.current = command;
    void (async () => {
      const { runInTerminal } = await import('@src/terminal/run-in-terminal');
      await runInTerminal(sessionId, command);
      navigation.openDock(currentDock.withoutShellStartCommand(), undefined, { replace: true });
    })().catch((err: unknown) => {
      startCommandRef.current = null;
      console.warn('[InteractiveTerminal] start command failed', err);
    });
  }, [active, shellReady, sessionId, currentDock, navigation]);

  const reportFirstPromptIfNeeded = useCallback(
    (prompt: string) => {
      const submittedPrompt = prompt.trim();
      if (!process || firstPromptReportedRef.current || !submittedPrompt) return;
      firstPromptReportedRef.current = true;
      void process
        .reportEvent(AgenticProcessEventName.FirstPrompt, {
          prompt: submittedPrompt,
          sent_at: new Date().toISOString(),
        })
        .then((result) => {
          console.debug('[InteractiveTerminal] first_prompt report_event accepted', result);
        })
        .catch((err: unknown) => {
          firstPromptReportedRef.current = false;
          console.warn('[InteractiveTerminal] first_prompt report_event failed', err);
        });
    },
    [process],
  );

  const collectFirstPromptInput = useCallback(
    (data: string) => {
      if (firstPromptReportedRef.current || !data || data.startsWith('\x1b')) return;
      let buffer = firstPromptBufferRef.current;
      for (const ch of data) {
        if (ch === '\r' || ch === '\n') {
          reportFirstPromptIfNeeded(buffer);
          buffer = '';
          continue;
        }
        if (ch === '\u007f' || ch === '\b') {
          buffer = buffer.slice(0, -1);
          continue;
        }
        if (ch >= ' ' && ch !== '\u007f') {
          buffer += ch;
        }
      }
      firstPromptBufferRef.current = buffer;
    },
    [reportFirstPromptIfNeeded],
  );

  // Input handler
  useEffect(() => {
    if (!sessionId || !terminalReady || !terminalRef.current) return;

    const term = terminalRef.current;
    const disp = term.onData(async (data: string) => {
      // eslint-disable-next-line no-control-regex
      if (/^\x1b\[\?[0-9;]*c$/.test(data)) return;
      collectFirstPromptInput(data);
      if (data.includes('\r') || data.includes('\n')) scheduleEnterRefetch();
      const shell = shellRef.current;
      if (shell) {
        // The connection decides: send now, hold as typeahead until its first
        // attach, or refuse on a pane that was live and dropped.
        inputDropRunRef.current = false;
        await shell.sendInput(data);
      } else if (!inputDropRunRef.current) {
        // No shell entity yet — nothing to hold the keystrokes for.
        inputDropRunRef.current = true;
        toplog.log('pty', `input_dropped shell=${sessionId} reason=no_shell`);
      }
    });

    return () => disp.dispose();
  }, [collectFirstPromptInput, scheduleEnterRefetch, terminalReady, sessionId]);

  // On session restart: clear terminal, reset pty-sync, and re-fit so the
  // resumed session starts on a clean, full-size canvas.
  useEffect(() => {
    if (!process) return;

    const handleRestarted = () => {
      const term = terminalRef.current;
      ptySyncRef.current.resetSession();

      // attachPty({ force: true }) resets attach state, then re-attaches
      // and re-subscribes the output handler once the attach completes.
      // The re-attach races the restart's PTY lifecycle (the old PTY is killed
      // and the new one is still spawning), so a transient "PTY not found"
      // rejection is expected and recoverable — the subsequent attach (on the
      // settled session) reconnects. Swallow it so it doesn't surface as an
      // uncaught page error; a real, persistent failure shows as a blank
      // terminal the user can click to reattach (TerminalRuntimeErrorBanner).
      const shell = shellRef.current;
      void shell
        ?.attachPty({ cols: term?.cols ?? 80, rows: term?.rows ?? 24, force: true })
        .catch((err) => console.debug('[InteractiveTerminal] restart re-attach deferred:', err));

      // Re-fit after a frame so xterm recalculates row/col geometry, and size the new PTY to it
      requestAnimationFrame(fitAndSync);
    };

    process.on('restarted', handleRestarted);
    return () => {
      process.off('restarted', handleRestarted);
    };
  }, [process, fitAndSync]);

  // ResizeObserver (only when active)
  useEffect(() => {
    if (!canUseDOM) return;
    if (!xtermContainerRef.current) return;

    const container = xtermContainerRef.current;

    // Circuit-breaker for the self-referential RO loop: fit()/rebuild()/setCellHeight
    // all mutate DOM *inside* the container but leave the container's own box size
    // unchanged. So we act ONLY when the observed box actually changed from the last
    // size we fit to — fit()'s own writes (and a sibling panel / modal that perturbs
    // the page, e.g. a portal toggling body overflow) can re-fire the observer but
    // find the box unchanged and bail, converging in one pass instead of pegging.
    let lastW = -1;
    let lastH = -1;

    const observer = new ResizeObserver(() => {
      // Off screen (inactive, or the window hidden) the view does not size the PTY, so it does
      // not fit: the box is left unrecorded and the return to screen fits + sends.
      if (!active || !onScreen()) return;
      if (isTransitioningRef.current) return;

      const w = container.clientWidth;
      const h = container.clientHeight;
      if (Math.abs(w - lastW) < 1 && Math.abs(h - lastH) < 1) return;
      lastW = w;
      lastH = h;

      // Fit and send TOGETHER, once the box settles: a fit whose send is dropped (the tab left
      // within the debounce) left the xterm on a grid the PTY never got.
      if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
      resizeTimeoutRef.current = setTimeout(() => {
        resizeTimeoutRef.current = null;
        if (isTransitioningRef.current) return;
        fitAndSync();
        // Rebuild VirtualTerminal with new dimensions and replay stored chunks
        const term = terminalRef.current;
        const shell = shellRef.current;
        const snapshot = ptySyncRef.current.getSnapshot();
        if (!term || !shell || !snapshot.adapter || !snapshot.vt) return;
        const chunks = shell.getPtyChunks();
        // Nothing to replay (an ended session let its window go): the VT keeps its last build.
        if (!chunks.length) return;
        const tRebuild = performance.now();
        ptySyncRef.current.rebuild(chunks);
        const rebuildMs = performance.now() - tRebuild;
        // Replays the shell's chunk window (bounded, see PtyConnection) — only slow ones are logged.
        if (rebuildMs > 50) {
          toplog.log(
            'pty',
            `vt_rebuild_slow shell=${sessionId} chunks=${chunks.length} ms=${rebuildMs.toFixed(0)} size=${term.cols}x${term.rows}`,
          );
        }
      }, 250);
    });

    observer.observe(container);
    return () => {
      observer.disconnect();
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
        resizeTimeoutRef.current = null;
      }
    };
  }, [active, fitAndSync, sessionId, canUseDOM, terminalReady]);

  // `tab_switch` warm chat: a return to a panel whose chat pane is already
  // mounted is a visibility flip — nothing reloads, so no other point fires.
  // Warm = shown, hidden, shown again. Keyed on having been HIDDEN, not on
  // having run before: StrictMode runs the mount effect twice, and a "ran
  // before" ref calls every fresh mount warm.
  const wasHiddenRef = useRef(false);
  useEffect(() => {
    if (!active) {
      wasHiddenRef.current = true;
      return;
    }
    if (!wasHiddenRef.current) return; // first showing — SimpleChatPane logs the cold ready
    wasHiddenRef.current = false;
    if (!showSimpleChat || !toplog.isOn('tab_switch')) return;
    const frame = requestAnimationFrame(() => {
      if (claimTabSwitchReady()) {
        toplog.log(
          'tab_switch',
          `ready ${sinceTabSwitch()} kind=chat mode=warm proc=${process?.id.slice(0, 8) ?? '-'}`,
        );
      }
    });
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per activation
  }, [active]);

  // Active tab focus/refresh — skip scrollToBottom when navigating to a bookmark
  // so the bookmark scroll (pendingScrollLine) can land without being overridden.
  useEffect(() => {
    if (!active) return;
    if (!terminalReady) return;

    const term = terminalRef.current;
    if (!term) return;

    requestAnimationFrame(() => {
      try {
        // Fit + size the PTY; a view that drifted while off screen asks for a redraw here.
        fitAndSync();
        if (!targetTimestamp) term.scrollToBottom();
        term.refresh(0, Math.max(0, term.rows - 1));
        focusTerminalUnlessEditingElsewhere(term, xtermContainerRef.current);
        // Warm = this panel already replayed its history; the refresh above is
        // the whole switch. A panel still attaching logs its cold `ready` later.
        if (shellReadyRef.current && !showSimpleChatRef.current && toplog.isOn('tab_switch') && claimTabSwitchReady()) {
          toplog.log('tab_switch', `ready ${sinceTabSwitch()} kind=terminal mode=warm shell=${sessionId}`);
        }
      } catch (e) {
        console.warn('[InteractiveTerminal] activate refresh failed:', e);
      }
    });
  }, [active, terminalReady, sessionId, fitAndSync, targetTimestamp]);

  // Re-assert this view's geometry to the backend PTY. Needed because the same
  // PTY can be attached in another tab with a different container size; whoever
  // resized last wins, leaving this xterm's cols/rows stale on the backend until
  // an input event happens to re-fit. On focus we re-fit and re-send our size —
  // even when it's unchanged locally, it differs from the backend's current
  // (other tab's) shape, so it triggers a SIGWINCH and repaints to our width.
  const reassertGeometry = useCallback(() => {
    if (!isTransitioningRef.current) fitAndSync();
  }, [fitAndSync]);

  // The tab coming back on screen re-asserts its size: the window regaining focus
  // or the browser tab turning visible changes neither `active` nor the layout,
  // so nothing else would notice another client resized the PTY meanwhile. Off
  // screen the view lets go, so a reconnect never re-asserts its stale grid.
  useEffect(() => {
    if (!terminalReady) return;
    if (!active) {
      shellRef.current?.releaseSize();
      return;
    }
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') shellRef.current?.releaseSize();
      else reassertGeometry();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', reassertGeometry);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', reassertGeometry);
    };
  }, [active, terminalReady, reassertGeometry]);
  useEffect(() => () => shellRef.current?.releaseSize(), []);

  const handleContainerClick = () => {
    terminalRef.current?.focus();
  };

  // Re-sync width whenever the terminal gains focus (click, tab, or programmatic).
  // xterm routes focus into a hidden <textarea> inside the container, so focusin
  // (which bubbles) captures every focus path.
  useEffect(() => {
    if (!terminalReady) return;
    const container = xtermContainerRef.current;
    if (!container) return;
    container.addEventListener('focusin', reassertGeometry);
    return () => container.removeEventListener('focusin', reassertGeometry);
  }, [terminalReady, reassertGeometry]);

  const handleFileDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      if (!inputDirInfo) return;
      const files = Array.from(e.dataTransfer.files);
      if (!files.length) return;
      const uploads = await fsStore.getState().uploadFiles(inputDirInfo.computeNodeTypeId, inputDirInfo.absPath, files);
      await Promise.all(uploads.map((u) => u.waitForCompletion()));
      for (const file of files) {
        const fullPath = `${inputDirInfo.absPath}/${file.name}`;
        await shellRef.current?.sendInput(`\nFile ${file.name} is available here: ${fullPath}\n`);
      }
      openSideTab(SideTabId.Files);
      requestAnimationFrame(() => terminalRef.current?.focus());
    },
    [inputDirInfo, openSideTab],
  );

  // "Paste path" from the Files tab: into the chat composer at its caret when
  // chat mode is on (the composer is mounted only then, and the chat layer
  // covers the PTY), else into the PTY.
  const composerRef = useRef<CompactExecutionInputHandle>(null);
  const handleInsertInputPath = useCallback((path: string) => {
    if (composerRef.current) {
      composerRef.current.insertAtCaret(path);
      return;
    }
    void shellRef.current?.sendInput(shellQuote(path));
    requestAnimationFrame(() => terminalRef.current?.focus());
  }, []);

  // Simple-chat composer image paste — same upload + Files-tab-open behaviour as
  // the PTY paste/drop handlers, but returns the reference line(s) so the chat
  // composer can splice them into the next prompt (instead of sending to a PTY).
  const handleChatPasteImages = useCallback(
    async (incoming: File[], { initialCaption }: { initialCaption?: string } = {}): Promise<string[]> => {
      if (!inputDirInfo || !incoming.length) return [];
      // Offer markup before the pasted image(s) are attached. Cancel aborts.
      const { files, caption } = await annotateImageFiles(incoming, { initialCaption });
      if (!files.length) return [];
      const uploads = await fsStore.getState().uploadFiles(inputDirInfo.computeNodeTypeId, inputDirInfo.absPath, files);
      await Promise.all(uploads.map((u) => u.waitForCompletion()));
      openSideTab(SideTabId.Files);
      const refs = files.map((file) => `File ${file.name} is available here: ${inputDirInfo.absPath}/${file.name}`);
      // The caption is the user's own prompt text — it follows the refs as-is.
      return caption ? [...refs, caption] : refs;
    },
    [inputDirInfo, openSideTab],
  );

  // Compute synthetic shell-pane active state for the ribbon
  const ribbonOpenTabs = sidecarShellId ? [...sideWindowTabs, SideTabId.Shell] : sideWindowTabs;
  const ribbonActiveSideTab = activePane === 'shell' && sidecarShellId ? SideTabId.Shell : activeSideTab;

  // Side-window panels keyed by tab id — only the active one is mounted by the
  // drawer. Null-guards mirror the prior inline conditionals.
  const sidePanels = useMemo<Partial<Record<SideTabId, React.ReactNode>>>(() => {
    const panels: Partial<Record<SideTabId, React.ReactNode>> = {
      [SideTabId.Git]: (
        <GitPanel
          computeNodeId={shellRef.current?.compute_node_id ?? dataContext.computeNode?.id ?? ''}
          workdir={shellRef.current?.workdir ?? process?.workdir ?? ''}
        />
      ),
      [SideTabId.Prompts]: (
        <PromptIndexPanel
          prompts={mergedPrompts}
          onScrollToLine={scrollAnnotationToLine}
          process={process ?? null}
          projectId={process?.project_id ?? null}
        />
      ),
    };
    if (process) {
      panels[SideTabId.Queue] = <QueuePanel process={process} />;
      panels[SideTabId.Context] = (
        <div className="h-full overflow-y-auto p-3">
          <EntityContextPanel entity={process} />
        </div>
      );
      panels[SideTabId.Analysis] = <AnalysisPanel process={process} />;
      panels[SideTabId.SkillsAgents] = (
        <SkillsAgentsPanel workerType={process.worker_type ?? null} sessionId={process.session_id ?? null} />
      );
    }
    if (inputDirInfo) {
      panels[SideTabId.Files] = (
        <InputFilesPanel
          computeNodeTypeId={inputDirInfo.computeNodeTypeId}
          inputDirAbsPath={inputDirInfo.absPath}
          onInsertPath={handleInsertInputPath}
        />
      );
      if (process?.workdir || shellRef.current?.workdir) {
        panels[SideTabId.Dir] = (
          <SimpleDirTree
            computeNodeTypeId={inputDirInfo.computeNodeTypeId}
            topLevel={process?.workdir ?? shellRef.current?.workdir ?? ''}
          />
        );
      }
    }
    return panels;
  }, [
    process,
    inputDirInfo,
    sidecarShellId,
    mergedPrompts,
    scrollAnnotationToLine,
    dataContext.computeNode?.id,
    handleInsertInputPath,
  ]);

  const sideTabs = useMemo<TabDescriptor<SideTabId>[]>(
    () =>
      sideWindowTabs.map((id) => {
        const d = SIDE_TABS[id];
        return {
          id,
          label: t(d.label),
          icon: d.icon,
          closable: true,
          tooltip: (
            <SideTabTooltipContent
              side="bottom"
              isPrompts={id === SideTabId.Prompts}
              lastPromptText={lastPromptText}
              promptCount={mergedPrompts.length}
              fallback={t(d.description)}
            />
          ),
        };
      }),
    [sideWindowTabs, lastPromptText, mergedPrompts.length, t],
  );

  return (
    <ChatPlanModeProvider process={process}>
      <div className={`relative flex h-full flex-col ${className}`} onDragOver={(e) => e.preventDefault()}>
        {/* Top bar — ProcessToolbar (Claude pane) or PaneBar (Shell pane) */}
        {process && activePane === 'claude' && (
          <FrozenWhenHidden active={active}>
            <ProcessToolbar
              process={process}
              traceFilters={traceFilters}
              onTraceFiltersChange={setTraceFilters}
              colVis={colVis}
              onColVisChange={setColVis}
              sessionStartTime={sessionStartTime}
              lastMessageTime={lastMessageTime}
              embedded={embedded}
              onClose={onClose}
              shell={shell}
            />
          </FrozenWhenHidden>
        )}
        {activePane === 'shell' && sidecarShellId && (
          <PaneBar label={t`Shell`} onClose={() => void handleKillSidecar()} />
        )}

        {/* Runtime-error banner — populated by the shell-dock loader on soft
          ProcessLoadError (PTY dead, process stopped, project missing,
          shell entity missing, network). Renders nothing when null, and
          nothing when the failure belongs to a DIFFERENT process: a plain
          terminal passes no id and so never wears another session's error. */}
        <TerminalRuntimeErrorBanner processId={process?.id} />

        <PtySyncProvider session={ptySyncRef.current}>
          {/* Column header — only for Claude pane; terminal debug chrome
            (trace/annotation/PTY-timing), hidden in Standard view and when the
            simple chat replaces the xterm. */}
          {process && activePane === 'claude' && !showSimpleChat && isAdvanced ? (
            <FrozenWhenHidden active={active}>
              <ColumnHeaderBar
                showTrace={showGutter}
                traceWidth={48}
                totalTraceEvents={totalTraceEvents}
                historicalCount={historicalCount}
                liveCount={liveCount}
                showTime={showTimeGutter}
                timeWidth={timeGutterWidth}
                traceFilters={traceFilters}
                showAnnotations={showAnnotationGutter}
                annotationsWidth={24}
                annotationElements={annotationElements}
                onToggleTrace={() => setColVis({ ...colVis, trace: !colVis.trace })}
                onHideTime={() => setColVis({ ...colVis, time: false })}
                onToggleAnnotations={() => setColVis({ ...colVis, annotations: !colVis.annotations })}
              />
            </FrozenWhenHidden>
          ) : null}

          <div className="flex min-h-0 flex-1">
            {/* Left pane selector strip — only when sidecar exists */}
            {sidecarShellId && <PaneSelectorBar activePane={activePane} onSelect={handlePaneSelect} />}

            {/* Claude pane — kept mounted, hidden when shell is active (preserves xterm) */}
            <div
              className="relative flex min-h-0 flex-1 flex-row"
              style={{ display: activePane !== 'claude' ? 'none' : undefined }}
            >
              {/* Relative container: xterm fills the space with padding for gutters;
                gutters are absolutely positioned over the padded areas.
                This prevents any gutter mount/unmount from changing xterm width.
                ``overflow-hidden`` clips gutter rows that overshoot the visible
                box (TimeGutter etc. render a fixed row count regardless of the
                container height — without clipping they paint over the footer). */}
              <div className="relative min-h-0 flex-1 overflow-hidden">
                {/* Wrapper reserves gutter space; xterm fills the content area only */}
                <div
                  className="absolute inset-0 flex"
                  style={{
                    paddingLeft:
                      (showGutter || reserveGutterSpace ? 48 : 0) +
                      (showTimeGutter || reserveTimeGutterSpace ? timeGutterWidth : 0),
                    paddingRight: showAnnotationGutter || reserveAnnotationSpace ? 24 : 0,
                  }}
                >
                  {/* Headless (pty_mode=false): don't render the xterm container at
                    all → the mount effect early-returns on the missing ref, so no
                    XTerm/PtySync is created. SimpleChatPane (absolute overlay
                    below) is the whole view. */}
                  {!isHeadless && (
                    <div
                      ref={xtermContainerRef}
                      className="relative min-h-0 min-w-0 flex-1"
                      onClick={handleContainerClick}
                      tabIndex={0}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        void handleFileDrop(e);
                      }}
                    >
                      {searchOpen && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <TerminalSearchBar
                            searchAddon={searchAddonRef.current}
                            onClose={() => {
                              setSearchOpen(false);
                              terminalRef.current?.focus();
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {terminalLinks.menu}
                {/* Gutters — absolutely positioned over padded areas */}
                {showGutter && (
                  <div
                    className="absolute bottom-0 left-0 top-0"
                    style={{ width: 48, zIndex: gutterExpanded ? 50 : 1 }}
                  >
                    <FrozenWhenHidden active={active}>
                      <TraceGutter
                        entries={gutterEntries}
                        totalTraceEvents={totalTraceEvents}
                        historicalCount={historicalCount}
                        liveCount={liveCount}
                        viewportY={viewportY}
                        rows={rows}
                        cellHeight={metricsCellHeight}
                        expanded={gutterExpanded}
                        onOpen={() => setGutterExpanded(true)}
                        onClose={() => setGutterExpanded(false)}
                        hideCounter
                      />
                    </FrozenWhenHidden>
                  </div>
                )}
                {showTimeGutter && (
                  <div className="absolute bottom-0 top-0" style={{ left: 48, width: timeGutterWidth, zIndex: 1 }}>
                    <FrozenWhenHidden active={active}>
                      <TimeGutter
                        rows={timeGutterRows}
                        cellHeight={metricsCellHeight}
                        filters={traceFilters}
                        ptySyncSession={ptySyncRef.current}
                        viewportY={viewportY}
                        refLines={ptySyncSnapshot.refLines}
                      />
                    </FrozenWhenHidden>
                  </div>
                )}
                {showAnnotationGutter && (
                  <div className="absolute bottom-0 right-0 top-0" style={{ width: 24, zIndex: 1 }}>
                    <FrozenWhenHidden active={active}>
                      <AnnotationGutter
                        elements={annotationElements}
                        viewportY={viewportY}
                        rows={rows}
                        cellHeight={metricsCellHeight}
                        scrollToLine={scrollAnnotationToLine}
                        createBookmark={createBookmark}
                        createComment={createComment}
                        deleteBookmark={deleteBookmark}
                        onHoverRow={onAnnotationHoverRow}
                        hideCounter
                      />
                    </FrozenWhenHidden>
                  </div>
                )}
                {/* A held-back switch, either direction: says why the pane has
                    not changed yet (renders nothing while nothing waits). */}
                {!embedded && (
                  <div className="absolute inset-x-0 top-0 z-[61]">
                    <SurfaceSwitchWaitNotice processId={process?.id} />
                  </div>
                )}
                {/* Standard-view simple chat — opaque overlay above xterm +
                  gutters. The xterm stays mounted (and fitted) underneath so
                  toggling Advanced⇄Standard is instant and never resets the
                  terminal. Same session, same PTY (see SimpleChatPane). */}
                {mountChatPane && process && (
                  <div
                    className={showSimpleChat ? 'absolute inset-0 z-[60]' : 'invisible absolute inset-0 z-[60]'}
                    aria-hidden={!showSimpleChat}
                    data-testid="simple-chat-overlay"
                    data-shown={showSimpleChat ? 'true' : 'false'}
                  >
                    <FrozenWhenHidden active={active && showSimpleChat}>
                      <SimpleChatPane process={process} />
                    </FrozenWhenHidden>
                  </div>
                )}
              </div>

              {/* Side window (non-Shell tabs) */}
              {sideWindowTabs.length > 0 && activeSideTab && (
                <TabbedSideDrawer<SideTabId>
                  open
                  width="w-80"
                  tabs={sideTabs}
                  activeTab={activeSideTab}
                  onActiveTabChange={selectSideTab}
                  onCloseTab={closeSideTab}
                  onOpenChange={sideWindows.closeAll}
                  closeLabel={t`Close all side tabs`}
                  closeText={t`Close all`}
                  truncateLabels
                  scrollableTabs
                >
                  {sidePanels}
                </TabbedSideDrawer>
              )}
            </div>

            {/* Shell pane — full content area when active */}
            {activePane === 'shell' && sidecarShellId && (
              <PaneView>
                <ShellTerminal shellId={sidecarShellId} active={true} className="min-h-0 flex-1" />
              </PaneView>
            )}
          </div>
        </PtySyncProvider>

        {process && (
          <FrozenWhenHidden active={active}>
            <TerminalBottomRibbon
              fileCount={fileCount}
              isActive={processIsActive}
              promptCount={mergedPrompts.length}
              lastPromptText={lastPromptText}
              process={process}
              openTabs={ribbonOpenTabs}
              activeSideTab={ribbonActiveSideTab}
              onOpenSideTab={(tab) => {
                if (tab === SideTabId.Shell) {
                  void handleToggleSidecar();
                } else {
                  toggleSideTab(tab);
                }
              }}
              hasLastPlan={hasPlan}
              onOpenLastPlan={handleOpenLastPlan}
              artifacts={artifacts}
              onOpenArtifact={handleOpenArtifact}
              shown={shownStack}
              onOpenShown={handleOpenShown}
              composer={
                showSimpleChat && process ? (
                  <ChatComposerBar process={process} onPasteImages={handleChatPasteImages} composerRef={composerRef} />
                ) : undefined
              }
            />
          </FrozenWhenHidden>
        )}
      </div>
    </ChatPlanModeProvider>
  );
};

// Memoize to prevent unnecessary remounts when parent re-renders
export default React.memo(InteractiveTerminal, (prevProps, nextProps) => {
  const processIdSame = prevProps.flow?.typeId === nextProps.flow?.typeId;

  return (
    prevProps.sessionId === nextProps.sessionId &&
    processIdSame &&
    prevProps.className === nextProps.className &&
    prevProps.active === nextProps.active &&
    prevProps.process?.id === nextProps.process?.id
  );
});
